import { out } from './ui.js';

/**
 * Keys whose *string* values must never reach stdout.
 *
 * The API does not project its responses — `bank-accounts` and
 * `webhook-settings` are fetched with Prisma `include`, so encrypted columns
 * (`ingestSecret`, `authorizationSecret`, `telegramBotToken`, `wpSecret`) and
 * `verificationCode` travel on the wire even though the SDK's types never
 * declare them. `JSON.stringify` serializes the *runtime* object, so
 * TypeScript offers no protection here at all.
 */
const SECRET_KEY = /secret|token|password|authorization|verificationcode/i;

/**
 * Keys that match {@link SECRET_KEY} but carry no secret and are worth reading.
 *
 * `tokenPrefix` is the public 12-char prefix the dashboard shows;
 * `authorizationType` / `authorizationHeaderName` are configuration, not
 * credentials — redacting them would make `webhook settings --json` useless.
 */
const ALLOWED_KEYS = new Set([
  'tokenPrefix',
  'authorizationType',
  'authorizationHeaderName',
]);

const REDACTED = '[redacted]';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    (Object.getPrototypeOf(value) === Object.prototype ||
      Object.getPrototypeOf(value) === null)
  );
}

function walk(value: unknown, seen: WeakSet<object>): unknown {
  if (Array.isArray(value)) {
    if (seen.has(value)) return REDACTED;
    seen.add(value);
    return value.map((item) => walk(item, seen));
  }

  if (!isPlainObject(value)) return value;
  if (seen.has(value)) return REDACTED;
  seen.add(value);

  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    // Only strings are redacted: `hasWpSecret: false` and a null secret are
    // exactly the signal someone runs this command to see.
    if (
      typeof item === 'string' &&
      item !== '' &&
      SECRET_KEY.test(key) &&
      !ALLOWED_KEYS.has(key)
    ) {
      result[key] = REDACTED;
      continue;
    }
    result[key] = walk(item, seen);
  }
  return result;
}

/**
 * Deep-copy `value` with secret-looking string fields replaced.
 *
 * Non-plain objects (`Date`, class instances, `Buffer`) are passed through by
 * reference — `JSON.stringify` handles them the same way it always did, and
 * API responses are plain JSON.
 */
export function redactSecrets<T>(value: T): T {
  return walk(value, new WeakSet<object>()) as T;
}

/** The single `--json` output path. Every command must go through this. */
export function printJson(value: unknown): void {
  out(JSON.stringify(redactSecrets(value), null, 2));
}
